import { Box, Tooltip, Typography } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { WEB_BUILD, buildsAgree, describeBuild } from '../buildInfo';
import { useApiVersion } from '../hooks/api';
import { relative } from '../utils/format';

/**
 * What is actually running, for somebody looking at a screen that is wrong.
 *
 * **Two versions, not one, and that is the whole point.** The page and the API
 * are separate deployables: the browser holds whatever it last downloaded, and
 * the API holds whatever was last started. A single version string would be a
 * half-truth in exactly the situation this exists for.
 *
 * Both failure modes are real and have cost time here — a stale bundle
 * surviving a rebuild, and an API process still serving pre-change code after
 * what looked like a restart. Each presented as "the feature does not work".
 *
 * A mismatch is reported as a mismatch. Not being able to tell — because one
 * side is unstamped — is reported as its own state rather than as agreement,
 * since silently claiming a match is how a real mismatch would be missed on the
 * deployments least likely to carry a stamp.
 */
export function BuildVersion({ compact = false }: { compact?: boolean }) {
  const { data } = useApiVersion();
  const api = data?.api;
  const agree = api ? buildsAgree(WEB_BUILD, api) : null;

  const line = describeBuild(WEB_BUILD);
  /** The unabridged identity: what an engineer checks out. */
  const full = (b: typeof WEB_BUILD) =>
    b.release && b.commit && !b.release.includes(b.commit)
      ? `${b.release} · ${b.commit}`
      : (b.release ?? b.commit ?? 'unstamped');

  const detail = [
    `Page: ${full(WEB_BUILD)}${WEB_BUILD.branch ? ` (${WEB_BUILD.branch})` : ''}`,
    WEB_BUILD.builtAt ? `  built ${relative(WEB_BUILD.builtAt)}` : null,
    api ? `API: ${full(api)}${api.branch ? ` (${api.branch})` : ''}` : 'API: could not be reached',
    api?.builtAt ? `  built ${relative(api.builtAt)}` : null,
    data?.environment ? `Environment: ${data.environment}` : null,
    agree === false
      ? '\nThese are different builds. Reload the page; if it persists, the two are deployed from different commits.'
      : agree === null
        ? '\nOne of these carries no build stamp, so they cannot be compared.'
        : null,
  ]
    .filter((x): x is string => x !== null)
    .join('\n');

  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: 'pre-line' }}>{detail}</Box>}
      describeChild
      placement={compact ? 'right' : undefined}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: compact ? 'center' : undefined,
          gap: 0.5,
          px: compact ? 0 : 2,
          py: 0.75,
          cursor: 'default',
        }}
      >
        {/*
          The warning is shown only for a KNOWN mismatch. An unstamped build is
          not a fault — a developer machine is the common case — and an amber
          icon on every local run would teach people to ignore it, which is the
          one thing this must not do.
        */}
        {agree === false && (
          <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} color="warning" aria-hidden />
        )}
        {/*
          ⚠️ **AT 72px THE STRING IS REPLACED BY A CONTROL, never truncated — and that follows
          from this component's OWN rule.** `-dirty` becomes `(modified)` and is "part of the
          identity, not a footnote": a build made over uncommitted changes is not the commit it
          claims to be. Clipping it — which is what happened, measured at 82px inside a 72px
          rail — hides exactly the part that says the string is not to be trusted, so the reader
          sees a confident version that is wrong. An ellipsis is no better on an identifier
          somebody is about to quote.

          The mismatch WARNING is NOT hidden: it is the one actionable thing here, and it is an
          icon already. Everything else is in the tooltip, which carried the full identity, both
          builds and the environment before this change.
        */}
        {compact ? (
          agree === false ? null : (
            <InfoOutlinedIcon sx={{ fontSize: 15 }} color="disabled" aria-hidden />
          )
        ) : (
          <Typography
            variant="caption"
            color={agree === false ? 'warning.main' : 'text.secondary'}
            sx={{ fontFamily: 'var(--mono)', fontSize: 11 }}
            data-testid="build-version"
          >
            {agree === false ? `${line} ≠ ${describeBuild(api!)}` : line}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
